import { useEffect, useMemo, useState } from 'react'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded'
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import PlayCircleRoundedIcon from '@mui/icons-material/PlayCircleRounded'
import {
  setHelperReadinessItemDone,
  subscribeToHelperMeetings,
  type HelperMeetingItem,
  type HelperReadinessItem,
} from '../../services/helper/helperMeetingService'
import { Checkbox, Pressable } from '../Common'
import { HelperEmptyState } from './HelperEmptyState'
import { useNls } from '../../nls/nlsProvider'

const formatStart = (ms: number | null): string => {
  if (!ms) return 'Time TBD'
  const date = new Date(ms)
  const today = new Date()
  const same = date.toDateString() === today.toDateString()
  const day = same ? 'Today' : date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return `${day} · ${time}`
}

const formatDuration = (meeting: HelperMeetingItem): string => {
  if (!meeting.startsAt || !meeting.endsAt) return 'Duration TBD'
  const minutes = Math.max(1, Math.round((meeting.endsAt - meeting.startsAt) / 60_000))
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const remaining = minutes % 60
  return remaining ? `${hours}h ${remaining}m` : `${hours}h`
}

const providerLabel = (value: string): string => value
  .split('_')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
  .join(' ')

const formatCountdown = (ms: number | null): string | null => {
  if (!ms) return null
  const delta = ms - Date.now()
  if (delta < 0) {
    const hoursAgo = Math.abs(Math.round(delta / 3_600_000))
    if (hoursAgo < 1) return 'In progress'
    if (hoursAgo < 24) return `Started ${hoursAgo}h ago`
    return null
  }
  const mins = Math.round(delta / 60_000)
  if (mins < 1) return 'Starting now'
  if (mins < 60) return `In ${mins} min`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `In ${hours}h`
  const days = Math.round(hours / 24)
  return `In ${days}d`
}

const statusClasses = (status: string): string => {
  const normalized = status.toUpperCase()
  if (normalized === 'LIVE' || normalized === 'READY') {
    return 'border-[color-mix(in_srgb,var(--success)_30%,transparent)] bg-[var(--success-soft)] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]'
  }
  if (normalized === 'CANCELLED' || normalized === 'CONNECTION_INTERRUPTED') {
    return 'border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[var(--danger-soft)] text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]'
  }
  if (normalized === 'COMPLETED') {
    return 'border-[var(--border-subtle)] bg-[var(--surface-muted)] text-[var(--muted)]'
  }
  return 'border-[color-mix(in_srgb,var(--info)_30%,transparent)] bg-[var(--info-soft)] text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))]'
}

const ReadinessMeter = ({ done, total }: { done: number; total: number }) => {
  const { t } = useNls()
  const percent = total === 0 ? 100 : Math.round((done / total) * 100)
  return (
    <div className="grid grid-cols-[54px_minmax(0,1fr)] items-center gap-3">
      <div
        className="grid h-[54px] w-[54px] place-items-center rounded-full border border-[var(--border-subtle)]"
        style={{
          background: `conic-gradient(var(--info) ${percent}%, var(--surface-muted) 0)`,
        }}
        aria-label={`${percent}% ready`}
      >
        <div className="grid h-[42px] w-[42px] place-items-center rounded-full bg-[var(--surface-card)] text-[11px] font-black text-[var(--text-strong)]">
          {percent}%
        </div>
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[var(--muted)]">{t('NLSHelperMeetingReadinessTitle')}</p>
        <p className="mt-1 text-sm font-semibold text-[var(--text-strong)]">
          {total === 0 ? t('NLSHelperMeetingNoPrepChecklist') : t('NLSHelperMeetingReadinessDone', { done, total })}
        </p>
      </div>
    </div>
  )
}

const ReadinessRow = ({
  item,
  index,
  onToggle,
}: {
  item: HelperReadinessItem
  index: number
  onToggle: (index: number, done: boolean) => void
}) => (
  <div
    className={`flex items-center gap-3 rounded-2xl border px-3 py-2.5 transition-colors ${
      item.done
        ? 'bg-[var(--success-soft)] border-[color-mix(in_srgb,var(--success)_20%,transparent)]'
        : 'bg-[var(--surface-card)] border-[var(--border-subtle)] hover:bg-[var(--surface-hover)]'
    }`}
  >
    <Checkbox
      checked={item.done}
      onChange={(next) => onToggle(index, next)}
      label={
        <span className={`min-w-0 flex-1 text-sm leading-snug ${item.done ? 'line-through text-[var(--muted)]' : 'text-[var(--text-strong)]'}`}>
          {item.label}
        </span>
      }
      wrapperClassName="w-full flex items-center gap-3"
    />
  </div>
)

type Props = {
  workspaceId: string | null
  uid: string | null
  onOpenAgendaNode: (checklistItemId: string) => void
}

export const MeetingActionGateway = ({ workspaceId, uid, onOpenAgendaNode }: Props) => {
  const { t } = useNls()
  const [meetings, setMeetings] = useState<HelperMeetingItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pendingToggle, setPendingToggle] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!workspaceId || !uid) {
      setMeetings([])
      setLoading(false)
      return
    }
    setLoading(true)
    return subscribeToHelperMeetings(workspaceId, uid, (items) => {
      setMeetings(items)
      setLoading(false)
    })
  }, [workspaceId, uid])

  const { upcoming, past } = useMemo(() => {
    const now = Date.now()
    return {
      upcoming: meetings.filter((meeting) => (meeting.startsAt ?? Number.MAX_SAFE_INTEGER) >= now - 3_600_000),
      past: meetings.filter((meeting) => (meeting.startsAt ?? 0) < now - 3_600_000),
    }
  }, [meetings])

  const nextMeeting = upcoming[0] ?? null
  const dueToday = upcoming.filter((meeting) => {
    if (!meeting.startsAt) return false
    return new Date(meeting.startsAt).toDateString() === new Date().toDateString()
  }).length
  const readinessTotal = upcoming.reduce((acc, meeting) => acc + meeting.readinessChecklist.length, 0)
  const readinessDone = upcoming.reduce(
    (acc, meeting) => acc + meeting.readinessChecklist.filter((entry) => entry.done).length,
    0,
  )

  const handleToggle = async (meeting: HelperMeetingItem, index: number, done: boolean) => {
    setPendingToggle(`${meeting.id}-${index}`)
    setErrorMessage(null)
    try {
      await setHelperReadinessItemDone(meeting.id, index, done, meeting.readinessChecklist)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to update readiness.')
    } finally {
      setPendingToggle(null)
    }
  }

  if (!workspaceId) {
    return (
      <div className="grid flex-1 place-items-center rounded-[32px] border border-[var(--border-subtle)] bg-[var(--surface-card)] px-6 text-center">
        <div className="grid max-w-xs justify-items-center gap-3">
          <div className="grid h-16 w-16 place-items-center rounded-3xl border border-[color-mix(in_srgb,var(--info)_20%,transparent)] bg-[var(--info-soft)] text-[var(--info)]" /* mosaic-keep: icon */>
            <CalendarMonthRoundedIcon style={{ fontSize: '30px' }} />
          </div>
          <p className="text-sm font-semibold text-[var(--text-strong)]">{t('NLSHelperMeetingSelectWorkspace')}</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex flex-1 flex-col gap-4" data-testid="helper-meeting-loading">
        <div className="h-36 animate-pulse rounded-[32px] border border-[var(--border-subtle)] bg-[var(--surface-muted)]" />
        <div className="h-44 animate-pulse rounded-[28px] border border-[var(--border-subtle)] bg-[var(--surface-muted)]" />
        <div className="h-44 animate-pulse rounded-[28px] border border-[var(--border-subtle)] bg-[var(--surface-muted)]" />
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain pb-2" data-testid="helper-meeting-gateway">
      <section
        className="relative overflow-hidden rounded-[32px] border border-[var(--border-subtle)] bg-[var(--surface-card)] p-5 shadow-[var(--shadow-card)]"
      >
        <div className="relative z-[1] flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))]">{t('NLSHelperMeetingAssignedHeader')}</p>
            <h2 className="mt-1 text-2xl font-black leading-tight tracking-tight text-[var(--text-strong)]">
              {nextMeeting ? nextMeeting.title : t('NLSHelperMeetingNoneYet')}
            </h2>
            <p className="mt-2 text-sm font-medium text-[var(--muted)]">
              {nextMeeting ? formatStart(nextMeeting.startsAt) : t('NLSHelperMeetingAssignmentsWillAppear')}
            </p>
          </div>
          <div className="grid h-14 w-14 shrink-0 place-items-center rounded-3xl border border-[color-mix(in_srgb,var(--info)_20%,transparent)] bg-[var(--info-soft)] text-[var(--info)]" /* mosaic-keep: icon */>
            <EventAvailableRoundedIcon style={{ fontSize: '28px' }} />
          </div>
        </div>
        <div className="relative z-[1] mt-5 grid grid-cols-3 gap-2">
          <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2">
            <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[var(--muted)]">{t('NLSHelperMeetingsStatUpcoming')}</p>
            <p className="mt-1 text-lg font-black text-[var(--text-strong)]">{upcoming.length}</p>
          </div>
          <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2">
            <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[var(--muted)]">{t('NLSHelperMeetingsStatToday')}</p>
            <p className="mt-1 text-lg font-black text-[var(--text-strong)]">{dueToday}</p>
          </div>
          <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2">
            <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[var(--muted)]">{t('NLSHelperMeetingsStatReady')}</p>
            <p className="mt-1 text-lg font-black text-[var(--text-strong)]">{readinessTotal === 0 ? '—' : `${readinessDone}/${readinessTotal}`}</p>
          </div>
        </div>

      </section>

      {errorMessage ? (
        <div className="rounded-2xl border border-[color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[var(--danger-soft)] px-3 py-2 text-xs font-medium text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]" role="status">
          {errorMessage}
        </div>
      ) : null}

      {upcoming.length === 0 ? (
        <div className="grid min-h-[260px] place-items-center rounded-[30px] border border-dashed border-[var(--border-subtle)] bg-[var(--surface-card)] px-6 text-center">
          <HelperEmptyState
            icon={<CalendarMonthRoundedIcon />}
            title={t('NLSHelperMeetingNoUpcoming')}
            body={t('NLSHelperMeetingNoActiveOnCalendar')}
            className="flex-none px-0"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {upcoming.map((meeting) => {
            const itemTotal = meeting.readinessChecklist.length
            const itemDone = meeting.readinessChecklist.filter((entry) => entry.done).length
            const countdown = formatCountdown(meeting.startsAt)
            return (
              <article
                key={meeting.id}
                className="relative overflow-hidden rounded-[30px] border border-[var(--border-subtle)] bg-[var(--surface-card)] shadow-[var(--shadow-card)]"
                data-testid="helper-meeting-card"
              >
                <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[var(--info)] via-[var(--accent)] to-[var(--success)]" aria-hidden="true" />
                <div className="flex flex-col gap-4 p-5">
                  <header className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] ${statusClasses(meeting.status)}`}>
                          {meeting.status.toLowerCase().replace(/_/g, ' ')}
                        </span>
                        {countdown ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-[var(--muted)]">
                            <AccessTimeRoundedIcon style={{ fontSize: '13px' }} />
                            {countdown}
                          </span>
                        ) : null}
                      </div>
                      <h3 className="text-xl font-black leading-tight tracking-tight text-[var(--text-strong)]">
                        {meeting.title}
                      </h3>
                    </div>
                    {meeting.joinUrl ? (
                      <a
                        href={meeting.joinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-2xl px-4 text-sm font-black text-[var(--on-accent)] bg-gradient-to-r from-[var(--btn-primary-from)] to-[var(--btn-primary-to)] shadow-[var(--shadow-card)] transition-transform active:translate-y-px"
                        data-testid="helper-meeting-join"
                      >
                        <PlayCircleRoundedIcon style={{ fontSize: '18px' }} />
                        {t('NLSHelperMeetingJoin')}
                      </a>
                    ) : null}
                  </header>

                  <div className="grid gap-2 sm:grid-cols-3">
                    <div className="inline-flex min-w-0 items-center gap-2 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 text-xs text-[var(--text)]">
                      <CalendarTodayRoundedIcon className="shrink-0 text-[var(--info)]" style={{ fontSize: '16px' }} /* mosaic-keep: icon */ />
                      <span className="truncate font-semibold">{formatStart(meeting.startsAt)}</span>
                    </div>
                    <div className="inline-flex min-w-0 items-center gap-2 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 text-xs text-[var(--text)]">
                      <AccessTimeRoundedIcon className="shrink-0 text-[var(--accent)]" style={{ fontSize: '16px' }} /* mosaic-keep: icon */ />
                      <span className="truncate font-semibold">{formatDuration(meeting)}</span>
                    </div>
                    <div className="inline-flex min-w-0 items-center gap-2 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 text-xs text-[var(--text)]">
                      <GroupsRoundedIcon className="shrink-0 text-[var(--success)]" style={{ fontSize: '16px' }} /* mosaic-keep: icon */ />
                      <span className="truncate font-semibold">{meeting.host ? `Host ${meeting.host}` : providerLabel(meeting.sourceProvider)}</span>
                    </div>
                  </div>

                  <section className="rounded-[24px] border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-4">
                    <ReadinessMeter done={itemDone} total={itemTotal} />
                    {itemTotal > 0 ? (
                      <div className="mt-4 flex flex-col gap-2">
                        {meeting.readinessChecklist.map((item, index) => (
                          <ReadinessRow
                            key={`${meeting.id}-${index}`}
                            item={pendingToggle === `${meeting.id}-${index}` ? { ...item, done: !item.done } : item}
                            index={index}
                            onToggle={(idx, done) => void handleToggle(meeting, idx, done)}
                          />
                        ))}
                      </div>
                    ) : null}
                  </section>

                  {meeting.agendaNodes.length > 0 ? (
                    <section className="rounded-[24px] border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-4">
                      <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-[var(--muted)]">
                        <AssignmentTurnedInRoundedIcon style={{ fontSize: '16px' }} />
                        {t('NLSHelperMeetingAgenda')}
                      </div>
                      <div className="grid gap-2">
                        {meeting.agendaNodes.map((nodeId) => (
                          <Pressable
                            key={nodeId}
                            onClick={() => onOpenAgendaNode(nodeId)}
                            className="inline-grid min-h-11 grid-cols-[18px_minmax(0,1fr)_18px] items-center gap-2 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] px-3 text-left text-sm text-[var(--text-strong)] transition hover:bg-[var(--surface-hover)]"
                          >
                            <ArrowOutwardRoundedIcon style={{ fontSize: '16px', color: 'var(--muted)' }} />
                            <span className="truncate font-semibold">{nodeId.replace(/^debug-task-/, '').replace(/-/g, ' ')}</span>
                            <KeyboardArrowRightRoundedIcon style={{ fontSize: '18px', color: 'var(--muted)' }} />
                          </Pressable>
                        ))}
                      </div>
                    </section>
                  ) : null}
                </div>
              </article>
            )
          })}
        </div>
      )}

      {past.length > 0 ? (
        <details className="rounded-[28px] border border-[var(--border-subtle)] bg-[var(--surface-card)] px-4 py-3">
          <summary className="flex cursor-pointer items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-[var(--muted)]">
            <HistoryRoundedIcon style={{ fontSize: '16px' }} />
            {t('NLSHelperMeetingPastHeader', { count: past.length })}
          </summary>
          <ul className="mt-3 flex list-none flex-col gap-2 p-0">
            {past.map((meeting) => (
              <li key={meeting.id} className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-2 text-xs text-[var(--muted)]">
                <p className="font-semibold text-[var(--text-strong)]">{meeting.title}</p>
                <p className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-[var(--muted)]">{formatStart(meeting.startsAt)}</p>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  )
}
